/** The "Produce a file" choice shared by the task box and the schedule form,
 * plus the in-memory note of workspaces that refused it this session. */
import { useSyncExternalStore } from 'react';
import { BotApiError } from '../../../services/BotApiError';
import { Label } from '../../../shared/ui/label';
import type { BotTaskDeliverable } from '../../../services/BotTasksService';

export const DELIVERABLE_OPTIONS: { value: BotTaskDeliverable; label: string; noun: string }[] = [
  { value: 'xlsx', label: 'Spreadsheet (.xlsx)', noun: 'a spreadsheet' },
  { value: 'docx', label: 'Word document (.docx)', noun: 'a Word document' },
  { value: 'pdf', label: 'PDF', noun: 'a PDF' },
  { value: 'csv', label: 'CSV', noun: 'a CSV file' },
];

export const DELIVERABLE_HELP = "The bot's file is checked before the task finishes. This can take longer and cost more.";
export const DELIVERABLE_REFUSED = 'Producing a checked file is not available yet.';

/** The code is the contract; the sentence is the fallback for an older server. */
export const isDeliverableRefusal = (err: unknown): boolean =>
  err instanceof Error && ((err instanceof BotApiError && err.code === 'deliverable_unavailable') || err.message === DELIVERABLE_REFUSED);

/** The check round under way. `iterations` counts finished rounds, so a
 * revision after round 1 means round 2 is running. */
export const checkRoundUnderWay = (iterations: number): number => Math.min(2, Math.max(1, Math.floor(Number.isFinite(iterations) ? iterations : 0) + 1));

export const isDeliverable = (value: unknown): value is BotTaskDeliverable => DELIVERABLE_OPTIONS.some((option) => option.value === value);

/** "Produces a spreadsheet", or null for none or a value this build does not know. */
export const deliverableSummary = (value: string | null | undefined): string | null => {
  const noun = DELIVERABLE_OPTIONS.find((option) => option.value === value)?.noun;
  return noun ? `Produces ${noun}` : null;
};

const refused = new Set<string>();
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

/** Call with any error from a request that carried a deliverable. */
export function noteDeliverableRefusal(workspaceId: string, err: unknown) {
  if (!isDeliverableRefusal(err) || refused.has(workspaceId)) return;
  refused.add(workspaceId);
  listeners.forEach((fn) => fn());
}

export const useDeliverableOffered = (workspaceId: string) =>
  !useSyncExternalStore(subscribe, () => refused.has(workspaceId), () => false);

/** Test hook. */
export const resetDeliverableRefusals = () => { refused.clear(); listeners.forEach((fn) => fn()); };

const SELECT_CLASS = 'h-9 w-full rounded-md border border-separator bg-background px-2 text-base lg:text-sm cursor-pointer';

export function DeliverableSelect({ id, value, onChange }: { id: string; value: BotTaskDeliverable | ''; onChange: (value: BotTaskDeliverable | '') => void }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Produce a file</Label>
      <select id={id} className={SELECT_CLASS} value={value} onChange={(event) => onChange(isDeliverable(event.target.value) ? event.target.value : '')}>
        <option value="">No file</option>
        {DELIVERABLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      {value && <p className="text-xs text-muted-foreground">{DELIVERABLE_HELP}</p>}
    </div>
  );
}
